import {html, css, LitElement, nothing} from 'lit';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {OutsideClickBehavior} from '../behaviors/outside-click-behavior';

class KeliPanelConfigurationMenu extends OutsideClickBehavior(HotkeyBehavior(LitElement)) {
  static styles = css`
      :host {
          background-color: var(--keli-background-color);
          border: var(--keli-container-border);
          bottom: 1em;
          box-shadow: var(--keli-shadow-z1);
          box-sizing: border-box;
          color: var(--keli-text);
          cursor: default;
          display: block;
          font-size: .9em;
          font-weight: 300;
          min-height: 150px;
          overflow-y: auto;
          padding: 16px 0;
          position: absolute;
          right: 0;
          top: var(--keli-panel-title-height);
          transform-origin: center top;
          transform: scale(0);
          transition: all var(--keli-transition-fast);
          visibility: hidden;
          width: 300px;
          z-index: var(--keli-zindex-panel-configuration);
      }

      :host(.open) {
          transform: scale(1);
          visibility: visible;
      }

      #reset {
          color: var(--keli-a-color);
          cursor: pointer;
          display: block;
          padding: 7px 15px 7px 15px;
          text-align: center;
          text-decoration: underline;
          white-space: nowrap;
      }
  `;

  static properties = {
    controls: {type: Array, attribute: 'controls'},
    fromElement: {type: Object},
    opened: {type: Boolean},
    panel: {type: Object},
  };

  constructor() {
    super();
    this.controls = [];
  }

  render() {
    return html`
        <span id="reset" @click="${this.reset}">Reset to Default</span>
        <div id="items">
            ${this.opened && this.controls?.length ? this.controls.map(item => html`
                <keli-menu-item keliname="${item.keliname}"
                                caption="${item.caption ?? nothing}"
                                checked="${(item.checked ? '' : nothing)}"
                                show-toggle
                                @keli-menu-item-toggled="${this.handleItemToggled}"></keli-menu-item>`) : nothing}
        </div>`;
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    for (const property of changedProperties.keys()) {
      if (this[`${property}Changed`]) {
        this[`${property}Changed`](this[property]);
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '-1');
      this.tabIndex = -1;
    }

    this.addEventListener('outside-click', this.handleOutsideClick);
    this.registerHotkey(KeliInterface.Hotkeys.browse.menu.close, {
      down: () => {
        this.opened = false;
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('outside-click', this.handleOutsideClick);
    this.fromElement = null;
    this.controls = null;
  }

  handleItemToggled(event) {
    const target = getEventTarget(event);
    const control = this.controls.find(item => item.keliname === target.keliname);
    if (control) {
      control.checked = target.checked;
    }

    this.dispatchEvent(new CustomEvent('keliPanelObjectVisibilityToggled', {
      bubbles: true, composed: true,
      detail: event.detail
    }));
  }

  handleOutsideClick() {
    this.opened = false;
  }

  openedChanged(newValue) {
    this.classList.toggle('open', newValue);
    if (newValue) {
      this.outsideClickElements = [this.fromElement];
      this.outsideClickListening = true;
      this.focus();
    } else {
      this.panel.dispatchEvent(new CustomEvent('globalFocusReleased', {bubbles: true, composed: true}));
      this.outsideClickListening = false;
    }
  }

  reset() {
    this.dispatchEvent(new CustomEvent('keliPanelObjectVisibilityResetAll', {bubbles: true, composed: true}));
    this.opened = false;
  }
}

customElements.define('keli-panel-configuration-menu', KeliPanelConfigurationMenu);